import type { BadgeVariant } from '../components/common/UIComponents';
import type {
  Suggestion,
  SuggestionWorkflowStatus,
  SuggestionWorkflowVersion,
} from '../types/firestore';

export type SuggestionWorkflowStage =
  | 'pre_vote'
  | 'voting'
  | 'post_vote'
  | 'implementation'
  | 'archived'
  | 'legacy';

export type SuggestionViewMode = 'original' | 'technical';

export interface SuggestionActiveContent {
  effectiveView: SuggestionViewMode;
  title: string;
  body: string;
  sectionLabel: string;
}

const normalizeLabel = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

const normalizeStatusKey = (value: unknown): string =>
  normalizeLabel(value)
    .toLowerCase()
    .replace(/[\s_]+/g, '_');

export const normalizeSuggestionWorkflowVersion = (value: unknown): SuggestionWorkflowVersion =>
  value === 2 ? 2 : 1;

export const normalizeSuggestionStatus = (value: unknown): SuggestionWorkflowStatus => {
  const normalized = normalizeStatusKey(value);
  switch (normalized) {
    case 'admin_review':
      return 'admin_review';
    case 'voting_open':
      return 'voting_open';
    case 'voting_closed_accepted':
      return 'voting_closed_accepted';
    case 'voting_closed_rejected':
      return 'voting_closed_rejected';
    case 'declined_by_admin':
      return 'declined_by_admin';
    case 'planned':
      return 'planned';
    case 'in_progress':
      return 'in_progress';
    case 'implemented':
      return 'implemented';
    case 'archived':
      return 'archived';
    case 'underconsideration':
      return 'underConsideration';
    case 'in_review':
      return 'in_review';
    case 'accepted':
      return 'accepted';
    case 'rejected':
      return 'rejected';
    case 'new':
    default:
      return 'new';
  }
};

export const getSuggestionWorkflowStage = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'status' | 'isArchived'>
): SuggestionWorkflowStage => {
  const status = normalizeSuggestionStatus(suggestion.status);
  const archived = suggestion.isArchived === true || status === 'archived';
  if (archived) {
    return 'archived';
  }

  if (normalizeSuggestionWorkflowVersion(suggestion.workflowVersion) === 1) {
    return 'legacy';
  }

  switch (status) {
    case 'new':
    case 'admin_review':
      return 'pre_vote';
    case 'voting_open':
      return 'voting';
    case 'voting_closed_accepted':
    case 'voting_closed_rejected':
    case 'declined_by_admin':
      return 'post_vote';
    case 'planned':
    case 'in_progress':
    case 'implemented':
      return 'implementation';
    default:
      return 'legacy';
  }
};

export const getSuggestionStatusLabel = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'status' | 'isArchived'>
): string => {
  const status = normalizeSuggestionStatus(suggestion.status);
  switch (status) {
    case 'new':
      return 'Nowa';
    case 'admin_review':
      return 'W opracowaniu';
    case 'voting_open':
      return 'Głosowanie';
    case 'voting_closed_accepted':
      return 'Przyjęta';
    case 'voting_closed_rejected':
      return 'Odrzucona po głosowaniu';
    case 'declined_by_admin':
      return 'Odrzucona przez admina';
    case 'planned':
      return 'Zaplanowana';
    case 'in_progress':
      return 'W realizacji';
    case 'implemented':
      return 'Wdrożona';
    case 'archived':
      return 'Archiwum';
    case 'in_review':
      return 'Weryfikowana';
    case 'underConsideration':
      return 'Rozpatrywana';
    case 'accepted':
      return 'Zaakceptowana';
    case 'rejected':
      return 'Odrzucona';
    default:
      return status;
  }
};

export const getSuggestionStatusVariant = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'status' | 'isArchived'>
): BadgeVariant => {
  const stage = getSuggestionWorkflowStage(suggestion);
  const status = normalizeSuggestionStatus(suggestion.status);

  if (stage === 'archived') {
    return 'neutral';
  }
  if (
    status === 'declined_by_admin' ||
    status === 'voting_closed_rejected' ||
    status === 'rejected'
  ) {
    return 'error';
  }
  if (status === 'voting_open') {
    return 'info';
  }
  if (
    status === 'planned' ||
    status === 'implemented' ||
    status === 'voting_closed_accepted' ||
    status === 'accepted'
  ) {
    return 'success';
  }
  if (
    status === 'new' ||
    status === 'admin_review' ||
    status === 'in_review' ||
    status === 'underConsideration'
  ) {
    return 'warning';
  }

  return 'neutral';
};

export const getSuggestionDisplayTitle = (
  suggestion: Pick<Suggestion, 'title' | 'originalTitle' | 'technicalTitle'>
): string => {
  return (
    normalizeLabel(suggestion.originalTitle) ||
    normalizeLabel(suggestion.technicalTitle) ||
    normalizeLabel(suggestion.title) ||
    'Bez tytułu'
  );
};

export const getSuggestionDisplaySummary = (
  suggestion: Pick<Suggestion, 'description' | 'originalDescription' | 'technicalSummary'>
): string => {
  return (
    normalizeLabel(suggestion.technicalSummary) ||
    normalizeLabel(suggestion.originalDescription) ||
    normalizeLabel(suggestion.description) ||
    'Brak opisu.'
  );
};

export const hasSuggestionTechnicalView = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'technicalTitle' | 'technicalSummary'>
): boolean =>
  normalizeSuggestionWorkflowVersion(suggestion.workflowVersion) === 2 &&
  Boolean(normalizeLabel(suggestion.technicalTitle) || normalizeLabel(suggestion.technicalSummary));

export const resolveSuggestionViewMode = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'technicalTitle' | 'technicalSummary'>,
  requestedView: SuggestionViewMode,
  preferredView: SuggestionViewMode = 'original'
): SuggestionViewMode => {
  const technicalAvailable = hasSuggestionTechnicalView(suggestion);

  if (requestedView === 'technical') {
    return technicalAvailable ? 'technical' : 'original';
  }

  if (requestedView === 'original') {
    return 'original';
  }

  return preferredView === 'technical' && technicalAvailable ? 'technical' : 'original';
};

export const getSuggestionDefaultView = (
  suggestion: Pick<Suggestion, 'workflowVersion' | 'technicalTitle' | 'technicalSummary'>,
  preferredView: SuggestionViewMode = 'original'
): SuggestionViewMode =>
  preferredView === 'technical' && hasSuggestionTechnicalView(suggestion)
    ? 'technical'
    : 'original';

export const resolveSuggestionActiveContent = (
  suggestion: Pick<
    Suggestion,
    | 'workflowVersion'
    | 'title'
    | 'description'
    | 'originalTitle'
    | 'originalDescription'
    | 'technicalTitle'
    | 'technicalSummary'
  >,
  requestedView: SuggestionViewMode
): SuggestionActiveContent => {
  const effectiveView = resolveSuggestionViewMode(suggestion, requestedView);
  const originalTitle =
    normalizeLabel(suggestion.originalTitle) ||
    normalizeLabel(suggestion.title) ||
    normalizeLabel(suggestion.technicalTitle) ||
    'Bez tytułu';
  const originalBody =
    normalizeLabel(suggestion.originalDescription) ||
    normalizeLabel(suggestion.description) ||
    normalizeLabel(suggestion.technicalSummary) ||
    'Brak opisu.';

  if (effectiveView === 'technical') {
    return {
      effectiveView,
      title: normalizeLabel(suggestion.technicalTitle) || originalTitle,
      body: normalizeLabel(suggestion.technicalSummary) || originalBody,
      sectionLabel: 'Wersja techniczna',
    };
  }

  return {
    effectiveView,
    title: originalTitle,
    body: originalBody,
    sectionLabel: 'Oryginał autora',
  };
};

export const getSuggestionAuthorLabel = (
  suggestion: Pick<Suggestion, 'createdByDisplayName' | 'userDisplayName' | 'createdBy' | 'userId'>
): string => {
  return (
    normalizeLabel(suggestion.createdByDisplayName) ||
    normalizeLabel(suggestion.userDisplayName) ||
    normalizeLabel(suggestion.createdBy) ||
    normalizeLabel(suggestion.userId) ||
    'Użytkownik'
  );
};

const toDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const candidate = value as {
    toDate?: () => Date;
  };

  if (typeof candidate.toDate === 'function') {
    try {
      const result = candidate.toDate();
      return result instanceof Date ? result : null;
    } catch {
      return null;
    }
  }

  return null;
};

export const formatSuggestionDate = (value: unknown): string => {
  const date = toDate(value);
  if (!date) {
    return 'Brak daty';
  }

  try {
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return 'Brak daty';
  }
};

export const formatSuggestionDateTime = (value: unknown): string => {
  const date = toDate(value);
  if (!date) {
    return 'Brak daty';
  }

  try {
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Brak daty';
  }
};

export const getSuggestionTurnoutPercent = (
  suggestion: Pick<Suggestion, 'eligibleVoterCountSnapshot' | 'turnoutCount'>
): number => {
  const eligible = Number(suggestion.eligibleVoterCountSnapshot || 0);
  const turnout = Number(suggestion.turnoutCount || 0);
  if (eligible <= 0) {
    return 0;
  }
  return Math.round((turnout / eligible) * 100);
};

export const getSuggestionVoteLabel = (vote: Suggestion['myVote']): string => {
  if (vote === 'yes') {
    return 'TAK';
  }
  if (vote === 'no') {
    return 'NIE';
  }
  return 'Brak';
};

export const getSuggestionVoteSummary = (
  suggestion: Pick<
    Suggestion,
    'yesCount' | 'noCount' | 'turnoutCount' | 'eligibleVoterCountSnapshot'
  >
): string => {
  const yesCount = Number(suggestion.yesCount || 0);
  const noCount = Number(suggestion.noCount || 0);
  const turnoutCount = Number(suggestion.turnoutCount || yesCount + noCount);
  const eligibleCount = Number(suggestion.eligibleVoterCountSnapshot || 0);

  if (eligibleCount <= 0) {
    return 'Brak snapshotu elektoratu';
  }

  const turnoutPercent = Math.round((turnoutCount / eligibleCount) * 100);
  return `TAK ${yesCount} / NIE ${noCount} / frekwencja ${turnoutPercent}%`;
};

export const getSuggestionDeadlineLabel = (
  suggestion: Pick<Suggestion, 'votingClosesAt'>
): string => {
  return formatSuggestionDateTime(suggestion.votingClosesAt);
};

export const isSuggestionVotingOpen = (suggestion: Pick<Suggestion, 'status'>): boolean =>
  normalizeSuggestionStatus(suggestion.status) === 'voting_open';

export const isSuggestionFrozen = (suggestion: Pick<Suggestion, 'status'>): boolean => {
  const status = normalizeSuggestionStatus(suggestion.status);
  return [
    'voting_open',
    'voting_closed_accepted',
    'voting_closed_rejected',
    'declined_by_admin',
    'planned',
    'in_progress',
    'implemented',
    'archived',
  ].includes(status);
};
